/* ========================================
   THEME.JS — Core Functionality
   Systemic Life Co. Art Gallery
   ======================================== */

(function () {
  'use strict';

  /* --- Header Scroll Behavior --- */
  function initHeader() {
    const header = document.querySelector('.site-header');
    if (!header) return;

    let lastScroll = 0;

    window.addEventListener('scroll', () => {
      const currentScroll = window.pageYOffset;
      if (currentScroll > 10) {
        header.classList.add('scrolled');
      } else {
        header.classList.remove('scrolled');
      }
      lastScroll = currentScroll;
    }, { passive: true });
  }

  /* --- Mobile Menu --- */
  function initMobileMenu() {
    const toggle = document.querySelector('.site-header__menu-toggle');
    const mobileNav = document.querySelector('[data-mobile-nav]');
    if (!toggle || !mobileNav) return;

    toggle.addEventListener('click', () => {
      const isOpen = toggle.classList.contains('is-open');
      toggle.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', !isOpen);
      mobileNav.classList.toggle('is-open');
      document.body.classList.toggle('no-scroll');
    });

    // Close on link click
    mobileNav.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', () => {
        toggle.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        mobileNav.classList.remove('is-open');
        document.body.classList.remove('no-scroll');
      });
    });
  }

  /* --- Cart Drawer --- */
  function initCartDrawer() {
    const overlay = document.querySelector('[data-cart-drawer-overlay]');
    const drawer = document.querySelector('[data-cart-drawer]');
    const openBtns = document.querySelectorAll('[data-cart-toggle]');
    const closeBtn = document.querySelector('[data-cart-drawer-close]');

    if (!overlay || !drawer) return;

    function openDrawer() {
      overlay.classList.add('is-open');
      drawer.classList.add('is-open');
      document.body.classList.add('no-scroll');
    }

    function closeDrawer() {
      overlay.classList.remove('is-open');
      drawer.classList.remove('is-open');
      document.body.classList.remove('no-scroll');
    }

    openBtns.forEach(btn => btn.addEventListener('click', openDrawer));
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    overlay.addEventListener('click', closeDrawer);

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });
  }

  /* --- Cart Quantity Changes (AJAX) --- */
  function initCartQuantity() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-cart-quantity-change]');
      if (!btn) return;

      e.preventDefault();
      const key = btn.dataset.cartQuantityChange;
      const change = parseInt(btn.dataset.quantityChange, 10);

      // Find current quantity
      const qtyEl = document.querySelector(`[data-cart-quantity-value="${key}"]`);
      if (!qtyEl) return;
      const currentQty = parseInt(qtyEl.textContent, 10);
      const newQty = currentQty + change;

      if (newQty < 0) return;

      try {
        if (newQty === 0) {
          // Remove item
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 })
          });
        } else {
          // Update quantity
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: newQty })
          });
        }

        // Refresh cart
        await refreshCart();
      } catch (err) {
        console.error('Cart update failed:', err);
      }
    });
  }

  /* --- Refresh Cart Drawer --- */
  async function refreshCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Update cart count in header
      const countEls = document.querySelectorAll('[data-cart-count]');
      countEls.forEach(el => {
        el.textContent = cart.item_count;
      });

      // Update drawer
      updateCartDrawer(cart);
    } catch (err) {
      console.error('Cart refresh failed:', err);
    }
  }

  function updateCartDrawer(cart) {
    const itemsContainer = document.querySelector('[data-cart-drawer-items]');
    const footerContainer = document.querySelector('[data-cart-drawer-footer]');
    const emptyContainer = document.querySelector('[data-cart-empty]');

    if (!itemsContainer) return;

    if (cart.item_count === 0) {
      itemsContainer.innerHTML = `
        <div class="cart-drawer__empty" data-cart-empty>
          <p>No works have been selected.</p>
          <a href="/collections" class="btn btn--secondary">View Collections</a>
        </div>
      `;
      if (footerContainer) footerContainer.style.display = 'none';
      return;
    }

    // Rebuild items
    let html = '';
    cart.items.forEach(item => {
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="cart-drawer__item-variant">${item.variant_title}</p>`
        : '';

      const imageHtml = item.image
        ? `<img src="${item.image}" alt="${item.title}" width="160" height="${Math.round(160 / (item.image ? 1 : 1))}" loading="lazy">`
        : '';

      html += `
        <div class="cart-drawer__item" data-cart-item="${item.key}">
          <div class="cart-drawer__item-image">${imageHtml}</div>
          <div class="cart-drawer__item-details">
            <div>
              <p class="cart-drawer__item-title">${item.product_title}</p>
              ${variantTitle}
            </div>
            <div class="cart-drawer__item-bottom">
              <div class="cart-drawer__quantity">
                <button class="cart-drawer__quantity-btn" data-cart-quantity-change="${item.key}" data-quantity-change="-1" aria-label="Decrease quantity">&minus;</button>
                <span class="cart-drawer__quantity-value" data-cart-quantity-value="${item.key}">${item.quantity}</span>
                <button class="cart-drawer__quantity-btn" data-cart-quantity-change="${item.key}" data-quantity-change="1" aria-label="Increase quantity">+</button>
              </div>
              <span class="cart-drawer__item-price" data-cart-item-price="${item.key}">${formatMoney(item.final_line_price)}</span>
            </div>
          </div>
        </div>
      `;
    });

    itemsContainer.innerHTML = html;

    // Update subtotal
    const subtotalEl = document.querySelector('[data-cart-subtotal]');
    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(cart.total_price);
    }

    if (footerContainer) footerContainer.style.display = 'block';
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /* --- Scroll Reveal Animations --- */
  function initReveal() {
    const elements = document.querySelectorAll('.reveal');
    if (!elements.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -40px 0px'
    });

    elements.forEach(el => observer.observe(el));
  }

  /* --- Initialize --- */
  function init() {
    initHeader();
    initMobileMenu();
    initCartDrawer();
    initCartQuantity();
    initReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
